import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiArrowUp, FiArrowDown, FiTrendingUp, FiDollarSign, FiCreditCard, FiPieChart, FiCalendar, FiActivity, FiTarget, FiBell, FiBarChart2, FiSettings, FiPlus, FiEdit, FiTrash2 } from '../utils/icons';
import Layout from '../components/layout/Layout';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import mascot from '../assets/placeholder.png';
import { useUserData } from '../utils/UserDataContext';
import Modal from '../components/modals/Modal';
import FinanceForm from '../components/forms/FinanceForm';

const Dashboard: React.FC = () => {
  const { userData, addTransaction, addBill, updateSavingsGoalProgress, addAsset } = useUserData();
  
  // Modal states
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [isBillModalOpen, setIsBillModalOpen] = useState(false);
  const [isAssetModalOpen, setIsAssetModalOpen] = useState(false);
  const [isSavingsModalOpen, setIsSavingsModalOpen] = useState(false);

  // Handle form submissions
  const handleAddTransaction = (formData: any) => {
    addTransaction({
      merchant: formData.merchant,
      amount: Number(formData.amount),
      date: formData.date,
      category: formData.category
    });
    setIsTransactionModalOpen(false);
  };

  const handleAddBill = (formData: any) => {
    addBill({
      name: formData.name,
      amount: Number(formData.amount),
      dueDate: formData.dueDate,
      recurring: formData.recurring === 'true',
      frequency: formData.frequency || 'monthly',
      paid: false
    });
    setIsBillModalOpen(false);
  };

  const handleAddAsset = (formData: any) => {
    addAsset({
      type: formData.type,
      name: formData.name,
      value: Number(formData.value),
      description: formData.description || ''
    });
    setIsAssetModalOpen(false);
  };

  const handleUpdateSavings = (formData: any) => {
    if (userData.finances.savingsGoals.length > 0) {
      updateSavingsGoalProgress(
        userData.finances.savingsGoals[0].id,
        Number(formData.currentAmount)
      );
    }
    setIsSavingsModalOpen(false);
  };

  return (
    <Layout>
      <div className="min-h-screen bg-background animate-fadeIn">
        <div className="container mx-auto px-4 py-8">
          {/* Top Section */}
          <div className="mb-8 animate-slideInFromTop">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary via-secondary to-accent p-0.5 animate-pulse-subtle">
                <div className="w-full h-full rounded-full bg-background flex items-center justify-center">
                  <img src={mascot} alt="Matrix Finance Mascot" className="w-8 h-8 object-contain" />
                </div>
              </div>
              <div>
                <h1 className="text-2xl font-bold">Welcome back, {userData.profile.name}!</h1>
                <p className="text-text/70">Let's manage your finances today</p>
              </div>
            </div>
          </div>

          {/* Transaction Modal */}
          <Modal
            isOpen={isTransactionModalOpen}
            onClose={() => setIsTransactionModalOpen(false)}
            title="Add New Transaction"
          >
            <FinanceForm
              title="Transaction Details"
              description="Record a new transaction to keep track of your spending."
              fields={[
                {
                  name: 'merchant',
                  label: 'Merchant',
                  type: 'text',
                  placeholder: 'e.g. Amazon, Starbucks',
                  required: true
                },
                {
                  name: 'amount',
                  label: 'Amount',
                  type: 'number',
                  placeholder: '0.00',
                  required: true
                },
                {
                  name: 'date',
                  label: 'Date',
                  type: 'date',
                  required: true,
                  defaultValue: new Date().toISOString().split('T')[0]
                },
                {
                  name: 'category',
                  label: 'Category',
                  type: 'select',
                  required: true,
                  options: [
                    { value: 'Shopping', label: 'Shopping' },
                    { value: 'Food & Drink', label: 'Food & Drink' },
                    { value: 'Transportation', label: 'Transportation' },
                    { value: 'Groceries', label: 'Groceries' },
                    { value: 'Entertainment', label: 'Entertainment' },
                    { value: 'Utilities', label: 'Utilities' },
                    { value: 'Housing', label: 'Housing' },
                    { value: 'Health', label: 'Health' },
                    { value: 'Education', label: 'Education' },
                    { value: 'Other', label: 'Other' }
                  ]
                }
              ]}
              submitLabel="Add Transaction"
              onSubmit={handleAddTransaction}
            />
          </Modal>

          {/* Bill Modal */}
          <Modal
            isOpen={isBillModalOpen}
            onClose={() => setIsBillModalOpen(false)}
            title="Add New Bill"
          >
            <FinanceForm
              title="Bill Details"
              description="Add a recurring bill or one-time payment."
              fields={[
                {
                  name: 'name',
                  label: 'Bill Name',
                  type: 'text',
                  placeholder: 'e.g. Rent, Internet',
                  required: true
                },
                {
                  name: 'amount',
                  label: 'Amount',
                  type: 'number',
                  placeholder: '0.00',
                  required: true
                },
                {
                  name: 'dueDate',
                  label: 'Due Date',
                  type: 'date',
                  required: true
                },
                {
                  name: 'recurring',
                  label: 'Is Recurring?',
                  type: 'select',
                  required: true,
                  options: [
                    { value: 'true', label: 'Yes' },
                    { value: 'false', label: 'No' }
                  ]
                },
                {
                  name: 'frequency',
                  label: 'Frequency',
                  type: 'select',
                  options: [
                    { value: 'monthly', label: 'Monthly' },
                    { value: 'quarterly', label: 'Quarterly' },
                    { value: 'yearly', label: 'Yearly' }
                  ]
                }
              ]}
              submitLabel="Add Bill"
              onSubmit={handleAddBill}
            />
          </Modal>

          {/* Asset Modal */}
          <Modal
            isOpen={isAssetModalOpen}
            onClose={() => setIsAssetModalOpen(false)}
            title="Add New Asset"
          >
            <FinanceForm
              title="Asset Details"
              description="Add a new asset to your portfolio."
              fields={[
                {
                  name: 'name',
                  label: 'Asset Name',
                  type: 'text',
                  placeholder: 'e.g. Checking Account, Bitcoin',
                  required: true
                },
                {
                  name: 'type',
                  label: 'Asset Type',
                  type: 'select',
                  required: true,
                  options: [
                    { value: 'cash', label: 'Cash' },
                    { value: 'investments', label: 'Investments' },
                    { value: 'crypto', label: 'Cryptocurrency' },
                    { value: 'realEstate', label: 'Real Estate' },
                    { value: 'other', label: 'Other' }
                  ]
                },
                {
                  name: 'value',
                  label: 'Current Value',
                  type: 'number',
                  placeholder: '0.00',
                  required: true
                },
                {
                  name: 'description',
                  label: 'Description',
                  type: 'textarea',
                  placeholder: 'Additional details about this asset...',
                  width: 'full'
                }
              ]}
              submitLabel="Add Asset"
              onSubmit={handleAddAsset}
            />
          </Modal>

          {/* Savings Goal Modal */}
          <Modal
            isOpen={isSavingsModalOpen}
            onClose={() => setIsSavingsModalOpen(false)}
            title="Update Savings Progress"
          >
            <FinanceForm
              title="Savings Update"
              description="Update your progress toward your savings goal."
              fields={[
                {
                  name: 'currentAmount',
                  label: 'Current Amount Saved',
                  type: 'number',
                  placeholder: '0.00',
                  required: true,
                  defaultValue: userData.finances.savingsGoals[0]?.currentAmount || 0
                }
              ]}
              submitLabel="Update Savings"
              onSubmit={handleUpdateSavings}
            />
          </Modal>

          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Financial Overview */}
            <div className="lg:col-span-2 space-y-6">
              {/* Account Balance Card */}
              <Card variant="glass" className="p-6 animate-slideInFromLeft stagger-item">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-lg font-medium text-text/70">Total Balance</h2>
                    <p className="text-3xl font-bold">${userData.finances.assets.total.toLocaleString()}</p>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    rightIcon={<FiPlus size={16} />}
                    onClick={() => setIsAssetModalOpen(true)}
                  >
                    Add Asset
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {Object.entries(userData.finances.assets.breakdown).filter(([key]) => key !== 'other' || userData.finances.assets.breakdown.other > 0).map(([key, value], index) => (
                    <div 
                      key={key} 
                      className={`bg-card/60 rounded-lg p-4 animate-slideInFromBottom stagger-item relative group`} 
                      style={{ animationDelay: `${index * 0.1 + 0.2}s` }}
                    >
                      <h3 className="text-sm font-medium text-text/70 capitalize">{key.replace(/([A-Z])/g, ' $1')}</h3>
                      <p className="text-lg font-semibold">${value.toLocaleString()}</p>
                      <div className="mt-2 h-1 bg-border rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-primary to-accent"
                          style={{ 
                            width: `${userData.finances.assets.total > 0 ? (value / userData.finances.assets.total) * 100 : 0}%`,
                            animation: 'shimmer 2s infinite'
                          }}
                        ></div>
                      </div>
                      
                      {/* Hidden action button that shows on hover */}
                      <button
                        className="absolute top-2 right-2 p-1 rounded-full bg-card opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label={`Manage ${key} assets`}
                        onClick={() => setIsAssetModalOpen(true)}
                      >
                        <FiEdit size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </Card>
              
              {/* Income vs Spending */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Card variant="glass" className="p-6 animate-slideInFromLeft stagger-item" style={{ animationDelay: '0.3s' }}>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-medium">Monthly Income</h2>
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center animate-pulse-subtle">
                      <FiArrowUp className="text-primary" size={20} />
                    </div>
                  </div>
                  <p className="text-2xl font-bold mb-1">${userData.finances.income.monthly.toLocaleString()}</p>
                  <p className="text-text/50 text-sm">
                    {userData.finances.income.sources.length > 1 
                      ? `From ${userData.finances.income.sources.length} sources`
                      : 'From primary job'
                    }
                  </p>
                  <Link to="/finances?tab=income">
                    <Button size="sm" variant="ghost" className="mt-4">
                      Manage income <FiArrowRight className="ml-2" size={14} />
                    </Button>
                  </Link>
                </Card>
                
                <Card variant="glass" className="p-6 animate-slideInFromRight stagger-item" style={{ animationDelay: '0.4s' }}>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-medium">Monthly Spending</h2>
                    <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center animate-pulse-subtle">
                      <FiArrowDown className="text-accent" size={20} />
                    </div>
                  </div>
                  <p className="text-2xl font-bold mb-1">${userData.finances.spending.monthly.toLocaleString()}</p>
                  <p className="text-text/50 text-sm">You spend {((userData.finances.spending.monthly / userData.finances.income.monthly) * 100).toFixed(0)}% of your income</p>
                  <Link to="/finances?tab=spending">
                    <Button size="sm" variant="ghost" className="mt-4">
                      View breakdown <FiArrowRight className="ml-2" size={14} />
                    </Button>
                  </Link>
                </Card>
              </div>
              
              {/* Recent Transactions */}
              <Card variant="glass" className="p-6 animate-slideInFromBottom stagger-item" style={{ animationDelay: '0.5s' }}>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-medium">Recent Transactions</h2>
                  <div className="flex space-x-2">
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => setIsTransactionModalOpen(true)}
                    >
                      <FiPlus size={16} className="mr-1" /> Add
                    </Button>
                    <Link to="/finances?tab=transactions">
                      <Button size="sm" variant="ghost">
                        View all
                      </Button>
                    </Link>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {userData.finances.transactions.slice(0, 4).map((transaction, index) => (
                    <div 
                      key={transaction.id} 
                      className="flex items-center justify-between py-3 border-b border-border last:border-0 animate-slideInFromRight stagger-item"
                      style={{ animationDelay: `${index * 0.1 + 0.6}s` }}
                    >
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-lg bg-card flex items-center justify-center mr-4">
                          <FiDollarSign size={18} />
                        </div>
                        <div>
                          <h3 className="font-medium">{transaction.merchant}</h3>
                          <p className="text-sm text-text/50">{transaction.category}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">-${transaction.amount.toLocaleString()}</p>
                        <p className="text-sm text-text/50">{transaction.date}</p>
                      </div>
                    </div>
                  ))}
                  
                  {userData.finances.transactions.length === 0 && (
                    <div className="py-6 text-center text-text/50">
                      <p>No transactions yet. Add your first one!</p>
                    </div>
                  )}
                </div>
              </Card>
            </div>
            
            {/* Right Column - Widgets */}
            <div className="space-y-6">
              {/* Savings Goal */}
              {userData.finances.savingsGoals.length > 0 && (
                <Card variant="glass" className="p-6 animate-slideInFromRight stagger-item" style={{ animationDelay: '0.2s' }}>
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-medium">Savings Goal</h2>
                    <Link to="/finances?tab=goals">
                      <Button size="sm" variant="ghost">
                        All Goals
                      </Button>
                    </Link>
                  </div>
                  
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-2xl font-bold">${userData.finances.savingsGoals[0].targetAmount.toLocaleString()}</p>
                      <p className="text-text/50 text-sm">{userData.finances.savingsGoals[0].name}</p>
                    </div>
                    <div className="w-16 h-16 rounded-full border-4 border-primary/20 flex items-center justify-center relative animate-pulse-subtle">
                      <svg viewBox="0 0 36 36" className="w-full h-full">
                        <path
                          className="stroke-primary"
                          fill="none"
                          strokeWidth="4"
                          strokeLinecap="round"
                          strokeDasharray={`${(userData.finances.savingsGoals[0].currentAmount / userData.finances.savingsGoals[0].targetAmount) * 100}, 100`}
                          transform="rotate(-90 18 18)"
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        />
                      </svg>
                      <span className="absolute text-sm font-semibold">
                        {Math.round((userData.finances.savingsGoals[0].currentAmount / userData.finances.savingsGoals[0].targetAmount) * 100)}%
                      </span>
                    </div>
                  </div>
                  
                  <div className="w-full h-2 bg-border rounded-full mb-2">
                    <div 
                      className="h-full bg-gradient-to-r from-primary to-accent rounded-full animate-shimmer"
                      style={{ width: `${(userData.finances.savingsGoals[0].currentAmount / userData.finances.savingsGoals[0].targetAmount) * 100}%` }}
                    ></div>
                  </div>
                  
                  <p className="text-text/70 text-sm mb-4">
                    You've saved ${userData.finances.savingsGoals[0].currentAmount.toLocaleString()} so far
                  </p>
                  
                  <Button fullWidth onClick={() => setIsSavingsModalOpen(true)}>Update Savings</Button>
                </Card>
              )}
              
              {/* Upcoming Bills */}
              <Card variant="glass" className="p-6 animate-slideInFromRight stagger-item" style={{ animationDelay: '0.3s' }}>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-medium">Upcoming Bills</h2>
                  <div className="flex space-x-2">
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => setIsBillModalOpen(true)}
                    >
                      <FiPlus size={16} className="mr-1" /> Add
                    </Button>
                    <Link to="/finances?tab=bills">
                      <Button size="sm" variant="ghost">
                        View all
                      </Button>
                    </Link>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {userData.finances.bills.slice(0, 3).map((bill, index) => (
                    <div 
                      key={bill.id} 
                      className="flex items-center justify-between py-3 border-b border-border last:border-0 animate-slideInFromRight stagger-item" 
                      style={{ animationDelay: `${index * 0.1 + 0.4}s` }}
                    >
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-lg bg-card flex items-center justify-center mr-4">
                          <FiCreditCard size={18} />
                        </div>
                        <div>
                          <h3 className="font-medium">{bill.name}</h3>
                          <p className="text-sm text-text/50">Due {bill.dueDate}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">${bill.amount.toLocaleString()}</p>
                        {bill.paid ? (
                          <span className="inline-block px-2 py-1 text-xs bg-green-500/20 text-green-500 rounded-full">Paid</span>
                        ) : (
                          <span className="inline-block px-2 py-1 text-xs bg-accent/20 text-accent rounded-full">Unpaid</span>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {userData.finances.bills.length === 0 && (
                    <div className="py-6 text-center text-text/50">
                      <p>No bills yet. Add your first one!</p>
                    </div>
                  )}
                </div>
                
                <Link to="/finances?tab=bills">
                  <Button variant="outline" fullWidth className="mt-4">
                    Manage All Bills
                  </Button>
                </Link>
              </Card>
              
              {/* Financial Tips */}
              <Card variant="glass" className="p-6 animate-slideInFromBottom stagger-item" style={{ animationDelay: '0.5s' }}>
                <h2 className="text-lg font-medium mb-4">Financial Tips</h2>
                <div className="space-y-4">
                  <div className="bg-card/60 rounded-lg p-4 animate-slideInFromBottom stagger-item" style={{ animationDelay: '0.6s' }}>
                    <div className="flex items-center mb-2">
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center mr-3 animate-pulse-subtle">
                        <FiTrendingUp className="text-primary" size={16} />
                      </div>
                      <h3 className="font-medium">Investment Opportunity</h3>
                    </div>
                    <p className="text-sm text-text/70 mb-3">
                      Based on your profile, consider adding more to your investment portfolio.
                    </p>
                    <Link to="/investments">
                      <Button size="sm" variant="ghost" className="text-primary flex items-center">
                        Learn more <FiArrowRight className="ml-1" size={14} />
                      </Button>
                    </Link>
                  </div>
                  
                  <div className="bg-card/60 rounded-lg p-4 animate-slideInFromBottom stagger-item" style={{ animationDelay: '0.7s' }}>
                    <div className="flex items-center mb-2">
                      <div className="w-8 h-8 rounded-full bg-secondary/20 flex items-center justify-center mr-3 animate-pulse-subtle">
                        <FiPieChart className="text-secondary" size={16} />
                      </div>
                      <h3 className="font-medium">Optimize Budget</h3>
                    </div>
                    <p className="text-sm text-text/70 mb-3">
                      Your dining expenses are 15% higher than last month.
                    </p>
                    <Link to="/budget">
                      <Button size="sm" variant="ghost" className="text-secondary flex items-center">
                        View budget <FiArrowRight className="ml-1" size={14} />
                      </Button>
                    </Link>
                  </div>
                  
                  <div className="bg-card/60 rounded-lg p-4 animate-slideInFromBottom stagger-item" style={{ animationDelay: '0.8s' }}>
                    <div className="flex items-center mb-2">
                      <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center mr-3 animate-pulse-subtle">
                        <FiCalendar className="text-accent" size={16} />
                      </div>
                      <h3 className="font-medium">Tax Planning</h3>
                    </div>
                    <p className="text-sm text-text/70 mb-3">
                      Start preparing your tax documents early this year.
                    </p>
                    <Link to="/taxes">
                      <Button size="sm" variant="ghost" className="text-accent flex items-center">
                        Get started <FiArrowRight className="ml-1" size={14} />
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* AI Assistant */}
          <Card className="mt-8 overflow-hidden animate-slideInFromBottom" style={{ animationDelay: '0.9s' }}>
            <div className="p-6 flex space-x-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary via-secondary to-accent p-0.5 flex-shrink-0 animate-pulse-subtle">
                <div className="w-full h-full rounded-full bg-background flex items-center justify-center">
                  <img src={mascot} alt="Matrix Finance Mascot" className="w-8 h-8 object-contain" />
                </div>
              </div>
              
              <div className="flex-1">
                <h3 className="text-lg font-semibold">Matrix AI Assistant</h3>
                <p className="text-text/70 mb-4">How can I help with your finances today?</p>
                
                <div className="space-y-2">
                  <button
                    className="w-full text-left px-4 py-2 rounded-lg bg-card-hover hover:bg-card-accent transition-colors flex items-center animate-slideInFromRight stagger-item"
                    onClick={() => {}}
                    style={{ animationDelay: '1s' }}
                  >
                    <span>💰</span>
                    <span className="ml-2">How can I optimize my savings?</span>
                    <FiArrowRight className="ml-auto" />
                  </button>
                  
                  <button
                    className="w-full text-left px-4 py-2 rounded-lg bg-card-hover hover:bg-card-accent transition-colors flex items-center animate-slideInFromRight stagger-item"
                    onClick={() => {}}
                    style={{ animationDelay: '1.1s' }}
                  >
                    <span>📊</span>
                    <span className="ml-2">Analyze my spending habits</span>
                    <FiArrowRight className="ml-auto" />
                  </button>
                  
                  <button
                    className="w-full text-left px-4 py-2 rounded-lg bg-card-hover hover:bg-card-accent transition-colors flex items-center animate-slideInFromRight stagger-item"
                    onClick={() => {}}
                    style={{ animationDelay: '1.2s' }}
                  >
                    <span>📈</span>
                    <span className="ml-2">Investment recommendations</span>
                    <FiArrowRight className="ml-auto" />
                  </button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard; 